import PropTypes from 'prop-types';

// @mui material components
import { Box, Card, Grid } from "@mui/material";

// Soft UI Dashboard PRO React components
import SoftBox from "components/SoftComponents/SoftBox";
import SoftTypography from "components/SoftComponents/SoftTypography";
import ClearwaysGroupCard from 'components/Groups/ClearwaysGroupCard';

import { useQuery } from '@tanstack/react-query'
import { useAuth0 } from "@auth0/auth0-react";

// API/MODELS
import groupAPI from "services/api/groups";
import bksAPI from "services/api/bks";

import { processGroupData } from "utils/group";
import { useMatch } from "react-router-dom";
import { disableMap } from "utils/map";
import { useSoftUIController } from "hooks/context";
import { wrapper, loadingScreen } from "utils/wrapper";
import { useEffect } from 'react';

function ClearwayDeviceSubgroupsView({ map, user }) {
  const [controller] = useSoftUIController();
  const { deviceLayout, selectedOrg, userPermissions, appearance } = controller;

  useEffect(() => {
    if (!map.current) return;
    disableMap(map, user, selectedOrg);
  }, [selectedOrg]);

  groupAPI.loadToken(useAuth0());
  bksAPI.loadToken(useAuth0());

  const showAdvancedDeviceLevel = userPermissions.indexOf('devices-advanced:view') >= 0;

  const { group_id } = useMatch('clearways/groups/:group_id/subgroups').params;

  const group = useQuery({
    queryKey: ['getGroupWithID', group_id, selectedOrg],
    queryFn: async () => {
      return await groupAPI.getGroupWithID(group_id);
    }
  });

  const devices = useQuery({
    queryKey: ['getAllDevices', selectedOrg],
    queryFn: async () => await bksAPI.getAllDevices(),
    refetchInterval: 300 * 1000,
    refetchIntervalInBackground: true,
  });

  if (group.isLoading || devices.isLoading) {
    return loadingScreen(controller, true);
  }

  if (group.error || devices.error) {
    return wrapper("An error has occurred", controller);
  }

  if (!group.data || !devices.data) {
    return loadingScreen(controller, false);
  }

  var group_data = processGroupData(group.dataUpdatedAt, group.data, devices.data, showAdvancedDeviceLevel);

  return wrapper((
    <SoftBox borderRadius="20px" color={"white"} width="100%" height={deviceLayout == 'mobile' ? 'calc(100svh - 4.25rem - 8px)' : "calc(100svh - 5.5rem - 16px)"} minHeight={deviceLayout == 'mobile' && "calc(100svh - 4.25rem - 8px)"}
      sx={({ palette, functions: { rgba } }) => ({ backgroundColor: rgba(palette[appearance].background, 0.7), overflowY: 'hidden' })}
    >
      <SoftBox mt={1} display="flex" sx={{ height: `calc(100% - 16px)` }}>
        <SoftBox py={0} px={1} width={"calc(100% - 16px)"} height="auto">
          <Card sx={({ palette, functions: { rgba } }) => ({ backgroundColor: rgba(palette[appearance].content, 1), height: '100%' })}>
            <SoftBox py={1} px={deviceLayout == 'mobile' ? 1 : 2} sx={({ palette, functions: { rgba } }) => ({ borderRadius: '16px 16px 0 0', backgroundColor: rgba(palette[appearance].header, 1), borderBottom: `1px solid ${rgba(palette[appearance].border, 1)}` })}>
              <SoftBox display="flex" flexDirection="column">
                <SoftTypography variant={deviceLayout == "mobile" ? "h5" : "h4"} fontWeight="bold" sx={{ mr: 0.5, flexShrink: 1, width: 'fit-content' }}>Subgroups</SoftTypography>
                <SoftTypography variant={deviceLayout == "mobile" ? "subtitle3" : "subtitle2"} fontWeight="medium" color="text" sx={{ minWidth: "fit-content" }}>
                  {group_data.name}
                </SoftTypography>
              </SoftBox>
            </SoftBox>
            <SoftBox p={1} sx={{ overflowY: 'auto' }}>
              <Grid container spacing={3}>
                {group_data.subgroups.map((subgroup) => (
                  <Grid item xxs={12} md={6} x3l={4} key={subgroup.group_id} onClick={() => { subgroup.group_id }} >
                    <ClearwaysGroupCard group={group_data} subgroup={subgroup} />
                  </Grid>
                ))}
              </Grid>
            </SoftBox>
          </Card>
        </SoftBox>
      </SoftBox>
    </SoftBox>
  ), controller);
}

ClearwayDeviceSubgroupsView.propTypes = {
  map: PropTypes.object,
  navTitle: PropTypes.array,
  setNavTitle: PropTypes.func,
  sidebarState: PropTypes.string,
  setSidebarState: PropTypes.func,
}

export default ClearwayDeviceSubgroupsView;
